'use client';
import { Stack } from '@mui/material';
import { provinces } from '@repo/onb-api';
import { formatter } from '@repo/utils';
import {
  getCityDataAction as getCityData,
  getCityDataByIdAction as getCityDataById,
  getIdByCityDataAction as getIdByCityData,
} from '@repo/utils/getCityData/actions';
import { type Ref, useEffect, useImperativeHandle, useState } from 'react';
import { Dropdown } from '../dropdown';

type ProvinceDropdownsProps = {
  id?: number;
  defaultProvince?: string;
  onDropdownChange?: () => void;
  onCompleted: (_result: number | undefined) => void;
  onBlur?: Function;
  ref?: Ref<ProvinceDropdownsRef>;
};

export type ProvinceDropdownsRef = {
  validate: () => boolean;
};

const formatOptions = (options: string[]) =>
  options
    .map((option) => ({
      label: formatter(option, 'capitalize'),
      value: option,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Renders a ProvinceDropdowns component with 3 required dropdowns with province, partido and locality selection.
 *
 * @param {number} id - The optional localityId to load initial data on each dropdown.
 * @param {string} defaultProvince - The optional string to preload the province dropdown.
 * @param {Function} onDropdownChange - The optional function to run with every dropdown change.
 * @param {Function} onCompleted - The function that runs when all dropdowns are completed which returns a localityId number.
 * @param {Function} onBlur - The function exposed to the form parent to trigger internal validation.
 * @return {JSX.Element} The rendered ProvinceDropdowns component.
 */
export const ProvinceDropdowns = ({
  id,
  defaultProvince,
  onDropdownChange,
  onCompleted,
  ref,
}: ProvinceDropdownsProps) => {
  const [province, setProvince] = useState(defaultProvince ?? '');
  const [provinceError, setProvinceError] = useState(false);
  const [partido, setPartido] = useState('');
  const [partidoOptions, setPartidoOptions] = useState<{ label: string; value: string }[]>([]);
  const [partidoError, setPartidoError] = useState(false);
  const [locality, setLocality] = useState('');
  const [localityOptions, setLocalityOptions] = useState<{ label: string; value: string }[]>([]);
  const [localityError, setLocalityError] = useState(false);
  const partidoDisabled = !province || partidoOptions?.length === 0;
  const localityDisabled = !partido || localityOptions?.length === 0;

  // Picking a locality resolves the localityId the parent form needs
  const selectLocality = (selectedLocality: string, selectedProvince: string, selectedPartido: string) => {
    setLocality(selectedLocality);
    setLocalityError(false);
    if (!selectedProvince || !selectedPartido) return;
    getIdByCityData({
      nombreProvincia: selectedProvince.replaceAll('_', ' '),
      nombrePartido: selectedPartido,
      nombreLocalidad: selectedLocality,
    })
      .then((value) => {
        onCompleted?.(value);
      })
      .catch((error) => console.error('Failed to get localityId with cityData', error));
  };

  // A single locality is picked for the user, there is nothing to choose
  const loadLocalities = (selectedProvince: string, selectedPartido: string) => {
    getCityData({
      filters: {
        nombrePartido: selectedPartido.replaceAll('_', ' '),
        nombreProvincia: selectedProvince.replaceAll('_', ' '),
      },
      targetKey: 'nombreLocalidad',
    })
      .then((result: string[]) => {
        const options = formatOptions(result);
        setLocalityOptions(options);
        if (options.length === 1) selectLocality(options[0]!.value, selectedProvince, selectedPartido);
      })
      .catch((error) => console.error(error));
  };

  const selectPartido = (selectedPartido: string, selectedProvince: string) => {
    setPartidoError(false);
    setPartido(selectedPartido);
    setLocality('');
    setLocalityError(false);
    loadLocalities(selectedProvince, selectedPartido);
    onDropdownChange?.();
  };

  // A single partido is picked for the user, which cascades into the localities
  const applyPartidoOptions = (result: string[], selectedProvince: string) => {
    const options = formatOptions(result);
    setPartidoOptions(options);
    if (options.length === 1) selectPartido(options[0]!.value, selectedProvince);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (id) {
          const cityData = await getCityDataById(id);
          if (!cityData) return;
          const { nombreProvincia, nombrePartido, nombreLocalidad } = cityData;
          setProvince(nombreProvincia);
          const partidos = await getCityData({
            filters: { nombreProvincia },
            targetKey: 'nombrePartido',
          });
          setPartidoOptions(formatOptions(partidos));
          setPartido(nombrePartido);
          const localities = await getCityData({
            filters: { nombreProvincia, nombrePartido },
            targetKey: 'nombreLocalidad',
          });
          setLocalityOptions(formatOptions(localities));
          setLocality(nombreLocalidad);
        } else if (!!defaultProvince && partidoOptions.length === 0) {
          const partidos = await getCityData({
            filters: { nombreProvincia: defaultProvince.replaceAll('_', ' ') },
            targetKey: 'nombrePartido',
          });
          applyPartidoOptions(partidos, defaultProvince);
        }
      } catch (error) {
        console.error('Failed to load initial city data:', error);
      }
    };
    loadInitialData();
  }, []);

  useImperativeHandle(ref, () => ({
    validate: () => {
      setProvinceError(!province);
      setPartidoError(partidoDisabled ? false : !partido);
      setLocalityError(localityDisabled ? false : !locality);
      return !(!province || !partido || !locality);
    },
  }));

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Dropdown
        label="Provincia"
        placeholder="Ej: Buenos Aires"
        value={province.toUpperCase().replaceAll('_', ' ')}
        data-testid="province"
        required
        error={provinceError}
        helperText={provinceError ? 'Seleccioná una provincia' : ''}
        options={provinces.map((p) => ({
          label: formatter(p, 'capitalize') as string,
          value: p.replaceAll('_', ' '),
        }))}
        onBlur={() => setProvinceError(!province)}
        onChange={(event) => {
          const value = (event.target as HTMLSelectElement)?.value;
          setProvince(value);
          setProvinceError(false);
          setPartido('');
          getCityData({
            filters: { nombreProvincia: value.replaceAll('_', ' ') },
            targetKey: 'nombrePartido',
          })
            .then((result: string[]) => applyPartidoOptions(result, value))
            .catch((error) => console.error(error));
          setLocality('');
          setLocalityOptions([]);
          onDropdownChange?.();
        }}
      />
      <Dropdown
        label="Partido"
        placeholder="Ej: Tigre"
        value={partido}
        data-testid="partido"
        required
        error={partidoError}
        helperText={partidoError ? 'Seleccioná un partido' : ''}
        disabled={partidoDisabled}
        options={partidoOptions ?? []}
        onBlur={() => setPartidoError(!!(province && !partido))}
        onChange={(event) => selectPartido((event.target as HTMLSelectElement)?.value, province)}
      />
      <Dropdown
        label="Localidad"
        placeholder="Ej: Don Torcuato"
        value={locality}
        data-testid="locality"
        required
        error={localityError}
        helperText={localityError ? 'Seleccioná una localidad' : ''}
        disabled={localityDisabled}
        options={localityOptions ?? []}
        onBlur={() => setLocalityError(!!(province && partido && !locality))}
        onChange={(event) => selectLocality((event.target as HTMLSelectElement)?.value, province, partido)}
      />
    </Stack>
  );
};
